import { Map } from 'mapbox-gl'

export function createHiddenDivAndAddToPage(mapWidth: number, mapHeight: number) {
  const hidden = document.createElement('div')
  hidden.style.overflow = 'hidden'
  hidden.style.position = 'fixed'
  hidden.style.width = `${mapWidth}px`
  hidden.style.height = `${mapHeight}px`
  document.body.appendChild(hidden)

  return hidden
}

export function deleteDiv(div: HTMLDivElement) {
  div.parentNode?.removeChild(div)
}

export const getMapImageAsBlob = async (map: Map): Promise<Blob> =>
  new Promise((resolve, reject) => {
    map.getCanvas().toBlob(async (imageBlob) => {
      if (imageBlob) resolve(imageBlob)
      else reject(new Error('Failed to get map image from canvas'))
    })
  })

export const getCanvasAsBlob = async (canvas: HTMLCanvasElement): Promise<Blob> =>
  new Promise((resolve, reject) => {
    canvas.toBlob((imageBlob) => {
      if (imageBlob) resolve(imageBlob)
      else reject(new Error('Failed to get image from canvas'))
    })
  })

export const compassSize = 50

export function makeCompass(angle: number) {
  return `<svg viewBox='0 0 ${compassSize} ${compassSize}' width='${compassSize}' height='${compassSize}' xmlns='http://www.w3.org/2000/svg' stroke-width='0.2'><rect width="100%" height="100%" fill="#ffffff" fill-opacity="0.6"/><g transform='translate(25, 25) scale(0.75 0.75) translate(-25, -25) rotate(${angle}, 25, 25)'><path stroke='black' fill='transparent' d='m 25,6.5 l -15,37 l 15,-7 z'/><path stroke='black' fill='black' d='m 25,6.5 l 15,37 l -15,-7 z'/></g></svg>`
}

export function makeScale(scale: number, pitch = 0) {
  let dist = 0.01,
    iters = 0,
    barDistance = 0,
    barWidth = 0

  const minWidth = 160,
    maxiters = 100,
    multiples = [1, 2, 4, 5]

  if (pitch > 15)
    return `<svg viewBox='0 0 ${minWidth + 20} ${compassSize}' width='${
      minWidth + 20
    }' height='${compassSize}' xmlns='http://www.w3.org/2000/svg' font-family='Inter, sans-serif' font-size='12' stroke-width='1'>
      <g>
        <rect width="100%" height="100%" fill="#ffffff" fill-opacity="0.6"/>
        <text x='22' y='28'>Scale varies in 3D view</text>
      </g>
    </svg>`

  while (barWidth < minWidth && iters < maxiters) {
    for (let i = 0, l = multiples.length; i < l; i++) {
      barDistance = dist * multiples[i]
      barWidth = barDistance / scale
      if (barWidth >= minWidth) break
    }

    dist *= 10
    iters++
  }

  let barDistanceInUnits = barDistance

  const unit = barDistance < 4000 ? 'Metres' : 'Kilometres'

  if (unit == 'Kilometres') barDistanceInUnits /= 1000

  //https://github.com/HarryStevens/d3-geo-scale-bar/blob/master/src/geoScaleBar.js
  return `
  <svg viewBox='0 0 ${barWidth + 20} ${compassSize}' width='${
    barWidth + 20
  }' height='${compassSize}' xmlns='http://www.w3.org/2000/svg' font-family='Inter, sans-serif' font-size='12' stroke-width='1'>
    <g>
      <rect width="100%" height="100%" fill="#ffffff" fill-opacity="0.6"/>
      <text x='5' y='15'>${unit}</text>
      <rect x='5'             y='20' width='${
        barWidth / 4
      }' height='12' fill='black' stroke='black'/>
      <rect x='${5 + barWidth / 4}' y='20' width='${
        barWidth / 4
      }' height='12' fill='transparent' stroke='black'/>
      <rect x='${5 + barWidth / 2}' y='20' width='${
        barWidth / 2
      }' height='12' fill='black' stroke='black'/>
      <text x='5'                   y='45' text-anchor='middle'>0</text>
      <text x='${5 + barWidth / 4}' y='45' text-anchor='middle'>${barDistanceInUnits / 4}</text>
      <text x='${5 + barWidth / 2}' y='45' text-anchor='middle'>${barDistanceInUnits / 2}</text>
      <text x='${5 + barWidth}' y='45' text-anchor='middle'>${barDistanceInUnits}</text>
    </g>
  </svg>`
}

const loadSvgAsImage = async (svg: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const image = new Image()
    image.onload = () => resolve(image)
    image.onerror = () => reject(new Error('Failed to load svg overlay'))
    image.src = 'data:image/svg+xml;base64,' + btoa(svg)
  })

const overlayGap = 5

// Composites the compass and scale bar (bottom-left, matching the layout used in
// explorer-api's PDF generation - see pdf-report-utils.ts / MapPage.ts) onto a copy
// of the map canvas, so both are baked into the exported image rather than sitting
// as separate DOM/SVG overlays.
export async function addCompassAndScaleToCanvas(
  mapCanvas: HTMLCanvasElement,
  scale: number,
  bearing: number,
  pitch: number,
): Promise<HTMLCanvasElement> {
  const compositeCanvas = document.createElement('canvas')
  compositeCanvas.width = mapCanvas.width
  compositeCanvas.height = mapCanvas.height

  const ctx = compositeCanvas.getContext('2d')
  if (!ctx) throw new Error('Failed to get canvas context for map export compositing')

  ctx.drawImage(mapCanvas, 0, 0)

  const [compassImage, scaleImage] = await Promise.all([
    loadSvgAsImage(makeCompass(-bearing)),
    loadSvgAsImage(makeScale(scale, pitch)),
  ])

  const overlayY = compositeCanvas.height - compassSize - overlayGap
  ctx.drawImage(compassImage, overlayGap, overlayY)
  ctx.drawImage(scaleImage, overlayGap + compassSize, overlayY)

  return compositeCanvas
}
